// src/components/common/BotBubble.jsx
/* ========================
 * BotBubble - Displays assistant messages in the chat feed
 * Enhances readability by adjusting padding for indented content
 * ========================*/
import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronUp, Brain, XCircle } from 'lucide-react';
import ActionButtons from './ActionButtons';
import MessageParser from './MessageParser';
import { parseFirestoreTimestamp } from '@/utils/timeUtils';
import LoadingDots from './LoadingDots';
import ThoughtAndMemoryPanel from './ThoughtAndMemoryPanel';
import { useTheme } from '@/managers/ThemeProvider';

// Maps error codes/messages to concise human-readable UI text.
// Title: what happened. Body: what to do or why.
function _errorTitle(errorDetails) {
  const code = errorDetails?.code || '';
  if (code === 'NETWORK_ERROR' || code === 'FETCH_FAILED') return 'No connection';
  if (code === 'CUSTOM_ENDPOINT_UNAVAILABLE') return 'Endpoint unavailable';
  if (code === 'CUSTOM_ENDPOINT_AUTH_ERROR') return 'Endpoint auth failed';
  if (code === 'CUSTOM_ENDPOINT_RATE_LIMIT') return 'Endpoint rate limited';
  if (code === 'CUSTOM_ENDPOINT_NOT_FOUND') return 'Endpoint not found';
  if (code === 'API_KEY_ERROR') return 'API key missing';
  if (code === 'INVALID_API_KEY') return 'Invalid API key';
  if (code === 'RATE_LIMIT_EXCEEDED') return 'Rate limit reached';
  if (code === 'QUOTA_EXCEEDED') return 'Quota exceeded';
  return 'Something went wrong';
}

function _errorBody(errorDetails) {
  const code = errorDetails?.code || '';
  const msg = errorDetails?.message || '';

  if (code === 'NETWORK_ERROR' || code === 'FETCH_FAILED' ||
      /failed to fetch|network error|load failed/i.test(msg)) {
    return 'Check your internet connection and try again.';
  }
  if (code === 'CUSTOM_ENDPOINT_UNAVAILABLE') {
    return 'The endpoint returned a server error (502/503). It may be overloaded or still starting up — try again in a moment.';
  }
  if (code === 'CUSTOM_ENDPOINT_AUTH_ERROR') {
    return 'The endpoint rejected the API key. Check your key in Settings → Custom Endpoints.';
  }
  if (code === 'CUSTOM_ENDPOINT_RATE_LIMIT') {
    return 'Too many requests to the endpoint. Wait a moment and try again.';
  }
  if (code === 'CUSTOM_ENDPOINT_NOT_FOUND') {
    return 'The endpoint URL returned a 404. Double-check the base URL in Settings → Custom Endpoints.';
  }
  if (code === 'API_KEY_ERROR') {
    return 'No API key found for this provider. Add your key in Settings.';
  }
  if (code === 'INVALID_API_KEY') {
    return 'The API key was rejected by the provider. Check it in Settings.';
  }
  if (code === 'RATE_LIMIT_EXCEEDED') {
    return 'Rate limit hit. Wait a moment and try again.';
  }
  if (code === 'QUOTA_EXCEEDED') {
    return 'You\'ve run out of credits with this provider. Check your account.';
  }

  // Last resort: show the message only if it's short and doesn't look like HTML/a stack trace
  if (msg && msg.length < 220 && !/<[a-z]/i.test(msg)) {
    return msg;
  }
  return 'An unexpected error occurred. Try sending your message again.';
}

const BotBubble = ({
  messageId,
  timestamp = new Date(),
  className = '',
  chatFontSize,
  cancelStream,
  onFixFormatting,
  onRedactContent,
  fullMessage,
  isLoadingOrStreaming,
  conversationStatus,
  onRewindToMessage,
  personalMemoryEnabled,
  isThoughtPanelCollapsed,
  onToggleThoughtPanel,
  thoughtSummary,
  isThinkingEnabled,
  contentBlocks,
  retrievedMemories,
  memoryStatus,
}) => {
  const isEffectivelyLoading = isLoadingOrStreaming && conversationStatus !== 'idle';

  const contentRef = useRef(null);
  const { isDarkTheme } = useTheme();

  const [isExpanded, setIsExpanded] = useState(true);
  const [showActionsForContent, setShowActionsForContent] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const handleCancel = useCallback(() => {
    if (cancelStream) {
      cancelStream(messageId);
    }
  }, [cancelStream, messageId]);

  const { thinkingBlocks, otherBlocks, hasThinkingBlock } = useMemo(() => {
    const thinking = [];
    const others = [];
    if (Array.isArray(contentBlocks)) {
      contentBlocks.forEach(block => {
        if (block.type === 'thinking') {
          thinking.push(block);
        } else {
          others.push(block);
        }
      });
    }
    return { thinkingBlocks: thinking, otherBlocks: others, hasThinkingBlock: thinking.length > 0 };
  }, [contentBlocks]);

  const parsedTimestamp = useMemo(() => parseFirestoreTimestamp(timestamp), [timestamp]);

  const { errorDetails, status: messageStatus } = fullMessage || {};

  const hasActualContentNow = otherBlocks.length > 0 && otherBlocks.some(b => (b.content && b.content.trim() !== '') || b.isStreamingImage || b.type === 'tool_code' || b.type === 'image');
  const actions = fullMessage?.actions || [];
  
  const isAbortedByUser = messageStatus === 'aborted_by_user' || errorDetails?.code === 'USER_CANCELLED';
  const messageHasError = errorDetails && (errorDetails.message || errorDetails.code) && !isAbortedByUser;
  const isErrorState = !!messageHasError;

  const memoriesHadError = memoryStatus === 'error';
  const hasSurfacedMemoriesContent = retrievedMemories && retrievedMemories.length > 0;
  
  const showThoughtAndMemoryComponent = hasThinkingBlock || memoriesHadError || hasSurfacedMemoriesContent;

  const handleDebugCopy = useCallback(async (event) => {
    if (event.shiftKey && event.detail === 2) {
      event.preventDefault();
      event.stopPropagation();
      const bubble = event.currentTarget;
      try {
        const debugData = { messageId, timestamp, status: messageStatus, fullMessage, contentBlocks, retrievedMemories, isLoadingOrStreaming: isEffectivelyLoading };
        const debugText = JSON.stringify(debugData, null, 2);
        await navigator.clipboard.writeText(debugText);
        console.group(`🐛 DEBUG: Message ${messageId}`);
        console.log('Full Message Object:', fullMessage);
        console.log('Retrieved Memories:', retrievedMemories);
        console.log('Debug JSON copied to clipboard ✅');
        console.groupEnd();
        if (bubble) {
            const originalStyle = bubble.style.transform;
            bubble.style.transform = 'scale(0.98)';
            bubble.style.transition = 'transform 0.1s ease';
            setTimeout(() => {
              bubble.style.transform = originalStyle;
              setTimeout(() => { bubble.style.transition = ''; }, 100);
            }, 100);
        }
      } catch (error) { console.error('Failed to copy debug data:', error); }
    }
  }, [messageId, timestamp, messageStatus, fullMessage, contentBlocks, isEffectivelyLoading, retrievedMemories]);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const checkOverflow = useCallback(() => {
    if (contentRef.current && hasActualContentNow && !isEffectivelyLoading) {
      const { scrollHeight, clientHeight } = contentRef.current;
      setIsOverflowing(scrollHeight > clientHeight + 2);
    } else {
      setIsOverflowing(false);
    }
  }, [hasActualContentNow, isEffectivelyLoading]);

  useEffect(() => {
    checkOverflow();
    const currentContentRef = contentRef.current;
    if (currentContentRef && hasActualContentNow && !isEffectivelyLoading) {
      const resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(currentContentRef);
      return () => {
        if (currentContentRef) resizeObserver.unobserve(currentContentRef);
        resizeObserver.disconnect();
      };
    }
  }, [checkOverflow, hasActualContentNow, otherBlocks, isEffectivelyLoading]);

  const handleBubbleMouseEnter = () => setShowActionsForContent(true);
  const handleBubbleMouseLeave = () => setShowActionsForContent(false);
  const handleExpandToggle = () => setIsExpanded(!isExpanded);
  
  const handleToggleThoughtCollapse = useCallback(() => {
    if (onToggleThoughtPanel) onToggleThoughtPanel(messageId);
  }, [onToggleThoughtPanel, messageId]);

  const showFooter = hasActualContentNow || otherBlocks?.some((b) => b.type === 'tool_code') || isErrorState || isAbortedByUser;
  const showClearButton = isErrorState && messageId && onRewindToMessage;

  const handleClearError = async () => {
    if (!showClearButton || isClearing) return;
    setIsClearing(true);
    try { await onRewindToMessage(messageId); } 
    catch (error) { console.error('Failed to clear error bubble by rewinding:', error); } 
    finally { setIsClearing(false); }
  };

  const showLoadingDots = isEffectivelyLoading && !hasActualContentNow;

  const getBubbleClasses = () => {
    if (showLoadingDots) return 'bg-agent-bubble ring-1 ring-white/5 text-agent-bubble-text';
    if (isErrorState) return 'bg-error-bubble ring-1 ring-error-bubble-border text-error-bubble-text min-w-[280px] max-w-[500px]';
    if (isAbortedByUser) return 'bg-cancelled-bubble ring-1 ring-cancelled-bubble-border text-cancelled-bubble-text min-w-[280px] max-w-[280px]';
    return 'bg-agent-bubble ring-1 ring-white/5 text-agent-bubble-text min-w-[300px] max-w-[97%]';
  };

  const renderContent = () => (
    <MessageParser
      contentBlocks={otherBlocks}
      messageId={messageId}
      attachments={fullMessage?.files || fullMessage?.mediaItems || []}
      isUserMessage={false}
      fontSize={chatFontSize}
      isLoadingOrStreaming={isEffectivelyLoading}
      onRedactContent={onRedactContent}
    />
  );

  return (
    <div
      id={messageId}
      className={`relative transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'} ${className}`}
      onMouseEnter={handleBubbleMouseEnter}
      onMouseLeave={handleBubbleMouseLeave}
      data-message-id={messageId}
      data-message-has-content={hasActualContentNow.toString()}
    >
      <div
        className={`relative rounded-2xl rounded-bl-md shadow-sm hover:shadow-md transition-shadow duration-200 cursor-default select-text ${getBubbleClasses()} ${isEffectivelyLoading && !hasActualContentNow ? 'px-4 py-3' : 'pl-4 pr-5 py-3'}`}
        onClick={handleDebugCopy}
      >
        <div>
          {showThoughtAndMemoryComponent && (
            <ThoughtAndMemoryPanel
              thoughtSummary={thoughtSummary}
              hasThinkingBlock={hasThinkingBlock}
              hasSurfacedMemories={hasSurfacedMemoriesContent}
              retrievedMemories={retrievedMemories}
              isLoadingOrStreaming={isEffectivelyLoading}
              memoriesHadError={memoriesHadError}
              isCollapsed={isThoughtPanelCollapsed}
              onToggleCollapse={handleToggleThoughtCollapse}
              isDarkTheme={isDarkTheme}
            />
          )}
          {showLoadingDots && <LoadingDots status={messageStatus} onCancel={handleCancel} />}
          {hasActualContentNow && !isErrorState && (
            <div
              ref={contentRef}
              className={`${!isExpanded && isOverflowing && !isEffectivelyLoading ? 'max-h-[300px] overflow-hidden' : ''}`}
            >
              {renderContent()}
            </div>
          )}
          {isErrorState && (
            <div className="flex flex-col gap-2 py-1">
              <div className="text-xs leading-relaxed">
                <strong className="font-semibold block pb-0.5">{_errorTitle(errorDetails)}</strong>
                <span className="opacity-90">{_errorBody(errorDetails)}</span>
              </div>
              {showClearButton && (
                <button
                  onClick={handleClearError}
                  disabled={isClearing}
                  className={`flex items-center justify-center gap-1 text-error-bubble-text/80 hover:text-error-bubble-text transition-colors px-2 py-1 rounded-md border border-error-bubble-border/50 hover:border-error-bubble-border/70 self-start ${isClearing ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isClearing ? <span className="animate-spin h-3 w-3 border-2 border-error-bubble-text/50 border-t-error-bubble-text rounded-full" /> : <XCircle size={12} />}
                  {isClearing ? 'Clearing...' : 'Clear Error'}
                </button>
              )}
            </div>
          )}
          {isAbortedByUser && <div className="text-xs leading-relaxed py-1 italic">Request cancelled.</div>}
        </div>
        {isOverflowing && hasActualContentNow && !isEffectivelyLoading && (
          <button
            onClick={handleExpandToggle}
            className="flex items-center gap-1 mt-2 text-xs hover:text-opacity-100 text-opacity-75 transition-colors"
          >
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            <span>{isExpanded ? 'Show less' : 'Show more'}</span>
          </button>
        )}
        {showFooter && (
          <div className="flex items-center justify-between mt-2 text-xs min-h-[20px]">
            <div className="flex items-center flex-nowrap gap-2 whitespace-nowrap shrink-0">
              {messageStatus === 'reformatting' || messageStatus === 'redacting' ? (
                <span className="text-xs text-on-surface-dim italic flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-on-surface-dim rounded-full animate-pulse opacity-75" />
                  {messageStatus === 'reformatting' ? 'Reformatting...' : 'Updating...'}
                </span>
              ) : (
                <span className="text-on-surface-dim whitespace-nowrap shrink-0">{parsedTimestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              )}
              <div className={`transition-opacity duration-200 ${(showActionsForContent || (isEffectivelyLoading && !isAbortedByUser)) ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <ActionButtons message={fullMessage} messageId={messageId} showActions={showActionsForContent} copyEnabled={!!hasActualContentNow} isUser={false} actions={actions} onCancel={handleCancel} onFixFormatting={onFixFormatting} isStreaming={isEffectivelyLoading && !isAbortedByUser} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ========================
 * areEqual - ARCHITECTURAL FIX
 * This is the new, intelligent comparison function that finally stops the re-render cascade.
 * It no longer blindly re-renders during streaming. Instead, it performs a targeted,
 * efficient check of the props that actually matter, preventing re-renders unless
 * the visible content has genuinely changed. This is the root fix for the flickering.
 * ========================*/
const areEqual = (prevProps, nextProps) => {
  // If loading state changes from loading to not-loading, we must re-render to show final state.
  if (prevProps.isLoadingOrStreaming && !nextProps.isLoadingOrStreaming) {
    return false;
  }

  // Props controlled by parent state that affect this bubble's appearance.
  if (
    prevProps.isThoughtPanelCollapsed !== nextProps.isThoughtPanelCollapsed ||
    prevProps.chatFontSize !== nextProps.chatFontSize
  ) {
    return false;
  }

  const prevMsg = prevProps.fullMessage;
  const nextMsg = nextProps.fullMessage;
  const prevBlocks = prevProps.contentBlocks;
  const nextBlocks = nextProps.contentBlocks;

  // Compare critical properties that define the message's state and content.
  if (
    prevMsg.id !== nextMsg.id ||
    prevMsg.status !== nextMsg.status ||
    prevProps.memoryStatus !== nextProps.memoryStatus ||
    (prevProps.retrievedMemories?.length || 0) !== (nextProps.retrievedMemories?.length || 0) ||
    (prevBlocks?.length || 0) !== (nextBlocks?.length || 0)
  ) {
    return false;
  }

  // If block counts are the same, check the content of the last block,
  // as this is the one that changes during streaming. This is the key
  // check that prevents re-renders for every minor streaming update.
  if (prevBlocks?.length > 0 && nextBlocks?.length > 0) {
    const lastPrevBlock = prevBlocks[prevBlocks.length - 1];
    const lastNextBlock = nextBlocks[nextBlocks.length - 1];
    if (lastPrevBlock.content !== lastNextBlock.content || lastPrevBlock.type !== lastNextBlock.type) {
      return false;
    }
  }

  // If all checks pass, the props are functionally identical. Skip the re-render.
  return true;
};


export default React.memo(BotBubble, areEqual);